import React, { useEffect, useMemo, useState } from "react";
import { Row, Col, Statistic, Typography, List, Space } from "antd";
import {
  UserOutlined,
  KeyOutlined,
  DollarOutlined,
  ProfileOutlined,
  FileTextOutlined,
  FileSearchOutlined,
} from "@ant-design/icons";
import { Card, Badge, Skeleton, PageHeader, EmptyState } from "../../components/Common";
import { useMutations } from "../../hooks/useApiMutation";
import useAccess, { MODULES } from "../../hooks/useAccess";
import dayjs from "dayjs";

const { Text } = Typography;

/* ── Accent tokens ── */
const ACCENT = {
  blue: {
    bg: "color-mix(in srgb, var(--accent-blue) 12%, transparent)",
    color: "var(--accent-blue)",
  },
  violet: {
    bg: "color-mix(in srgb, var(--accent-violet) 12%, transparent)",
    color: "var(--accent-violet)",
  },
  teal: {
    bg: "color-mix(in srgb, var(--accent-teal) 12%, transparent)",
    color: "var(--accent-teal)",
  },
  amber: {
    bg: "color-mix(in srgb, var(--accent-amber) 12%, transparent)",
    color: "var(--accent-amber)",
  },
  rose: {
    bg: "color-mix(in srgb, var(--accent-rose) 12%, transparent)",
    color: "var(--accent-rose)",
  },
};

/* ── KPI card ── */
const StatCard = ({ icon, label, value, loading, accent = "blue", suffix, warning }) => {
  const { bg, color } = ACCENT[accent] ?? ACCENT.blue;
  const isWarning = warning && value > 0;
  return (
    <Card padding="sm" style={{ height: "100%" }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Text
            style={{
              fontSize: 12,
              fontWeight: 600,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              color: "var(--text-secondary)",
              display: "block",
              marginBottom: 8,
            }}
          >
            {label}
          </Text>
          {loading ? (
            <div style={{ marginTop: 6 }}>
              <Skeleton rows={1} />
            </div>
          ) : (
            <Statistic
              value={value ?? 0}
              suffix={suffix ? <span style={{ fontSize: 14, fontWeight: 500 }}>{suffix}</span> : undefined}
              valueStyle={{
                fontSize: 26,
                fontWeight: 700,
                color: isWarning ? color : "var(--text-primary)",
                fontFamily: "'Montserrat', sans-serif",
                lineHeight: 1.2,
              }}
            />
          )}
        </div>
        <div
          style={{
            width: 44,
            height: 44,
            borderRadius: 12,
            background: bg,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 18,
            color,
            flexShrink: 0,
          }}
          aria-hidden="true"
        >
          {icon}
        </div>
      </div>
    </Card>
  );
};

/* ── Invoice status → Badge variant ── */
const invoiceVariant = (status) => {
  switch (status) {
    case "Paid":      return "success";
    case "Sent":      return "info";
    case "Cancelled": return "error";
    default:          return "neutral";
  }
};

/* ═══════════════════════════════════════════════════
   Dashboard — fetches data from existing endpoints
   (no dedicated /dashboard/stats needed)
═══════════════════════════════════════════════════ */
const Dashboard = () => {
  const { mutate } = useMutations();
  const { can } = useAccess();

  // The dashboard is the landing page for every back-office role, but each figure on it
  // comes from a module-gated endpoint. A developer scoped to a subset of modules would
  // otherwise be met by one 403 toast per tile they are not entitled to, so the tile and
  // the request behind it are dropped together.
  const grants = useMemo(
    () => ({
      clients:       can(MODULES.CLIENTS),
      subscriptions: can(MODULES.SUBSCRIPTIONS),
      payments:      can(MODULES.PAYMENTS),
      invoices:      can(MODULES.INVOICES),
      apiKeys:       can(MODULES.API_KEYS),
      logs:          can(MODULES.LOGS),
    }),
    [can]
  );

  const hasAnyGrant = Object.values(grants).some(Boolean);

  const [loadingState, setLoadingState] = useState(hasAnyGrant);
  const [stats, setStats] = useState({
    totalClients: 0,
    activeSubscriptions: 0,
    totalRevenue: 0,
    totalApiKeys: 0,
    totalInvoices: 0,
    logsToday: 0,
    recentInvoices: [],
    recentPayments: [],
  });

  useEffect(() => {
    if (!hasAnyGrant) {
      setLoadingState(false);
      return;
    }

    setLoadingState(true);

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const fetch = (key, endpoint, params) =>
      mutate(key, { method: "get", endpoint, params });

    // Stands in for a request that was never sent: the tile behind it is hidden, so the
    // slot resolves to nothing rather than to an error.
    const skipped = Promise.resolve(null);

    Promise.allSettled([
      grants.clients       ? fetch("dash_clients",  "/clients",      { page: 1, pageSize: 1 })                                 : skipped,
      grants.subscriptions ? fetch("dash_subs",     "/subscription", { page: 1, pageSize: 1, status: true })                   : skipped,
      grants.payments      ? fetch("dash_payments", "/payments",     { page: 1, pageSize: 5 })                                 : skipped,
      grants.invoices      ? fetch("dash_invoices", "/invoices",     { page: 1, pageSize: 5 })                                 : skipped,
      grants.apiKeys       ? fetch("dash_apikeys",  "/apikeys",      { page: 1, pageSize: 1 })                                 : skipped,
      grants.logs          ? fetch("dash_logs",     "/logs",         { page: 1, pageSize: 1, startDate: today.toISOString() }) : skipped,
    ]).then(([clients, subs, payments, invoices, apikeys, logs]) => {
      const get = (result) => result.status === "fulfilled" ? result.value?.data?.data : null;

      const clientsData   = get(clients);
      const subsData      = get(subs);
      const paymentsData  = get(payments);
      const invoicesData  = get(invoices);
      const apikeysData   = get(apikeys);
      const logsData      = get(logs);

      const totalRevenue = Array.isArray(paymentsData?.items)
        ? paymentsData.items.reduce((sum, p) => sum + (p.amount ?? 0), 0)
        : 0;

      setStats({
        totalClients:         clientsData?.totalCount  ?? 0,
        activeSubscriptions:  subsData?.totalCount     ?? 0,
        totalRevenue,
        totalApiKeys:         apikeysData?.totalCount  ?? 0,
        totalInvoices:        invoicesData?.totalCount ?? 0,
        logsToday:            logsData?.totalCount     ?? 0,
        recentInvoices:       invoicesData?.items      ?? [],
        recentPayments:       paymentsData?.items      ?? [],
      });
      setLoadingState(false);
    });
  // `mutate` is re-created on every render, so the grants drive the refetch instead.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    hasAnyGrant,
    grants.clients,
    grants.subscriptions,
    grants.payments,
    grants.invoices,
    grants.apiKeys,
    grants.logs,
  ]);

  const primaryCards = [
    grants.clients && {
      key: "clients",
      icon: <UserOutlined />,
      label: "Total Clients",
      value: stats.totalClients,
      accent: "blue",
    },
    grants.subscriptions && {
      key: "subscriptions",
      icon: <ProfileOutlined />,
      label: "Active Subscriptions",
      value: stats.activeSubscriptions,
      accent: "teal",
    },
    grants.payments && {
      key: "revenue",
      icon: <DollarOutlined />,
      label: "Recent 5 payments",
      value: Number(stats.totalRevenue).toFixed(2),
      suffix: "EUR",
      accent: "violet",
    },
    grants.apiKeys && {
      key: "apikeys",
      icon: <KeyOutlined />,
      label: "Active API Keys",
      value: stats.totalApiKeys,
      accent: "amber",
    },
  ].filter(Boolean);

  const secondaryCards = [
    grants.invoices && {
      key: "invoices",
      icon: <FileTextOutlined />,
      label: "Total Invoices",
      value: stats.totalInvoices,
      accent: "violet",
    },
    grants.logs && {
      key: "logs",
      icon: <FileSearchOutlined />,
      label: "Logs Today",
      value: stats.logsToday,
      accent: "blue",
    },
  ].filter(Boolean);

  const overviewCards = [...primaryCards, ...secondaryCards];

  return (
    <Space direction="vertical" size={20} style={{ width: "100%" }}>
      <PageHeader
        title="Dashboard"
        subtitle="Platform overview — clients, revenue and activity at a glance."
        className="page-header--flush"
      />

      {!hasAnyGrant && (
        <Card>
          <div style={{ padding: "32px 0" }}>
            <EmptyState
              title="Nothing to show here yet"
              description="Your account has no module with an overview attached. Use the menu on the left to reach the screens you were granted."
            />
          </div>
        </Card>
      )}

      {/* Compact overview — every permitted KPI keeps equal visual weight. */}
      {overviewCards.length > 0 && (
        <Row gutter={[16, 16]}>
          {overviewCards.map(({ key, ...card }) => (
            <Col key={key} xs={24} sm={12} lg={8} xl={4}>
              <StatCard {...card} loading={loadingState} />
            </Col>
          ))}
        </Row>
      )}

      {/* ── Row 3 — Recent activity ── */}
      {(grants.invoices || grants.payments) && (
      <Row gutter={[16, 16]}>

        {/* Recent Invoices */}
        {grants.invoices && (
        <Col xs={24} lg={grants.payments ? 12 : 24}>
          <Card
            padding="sm"
            title={<Text style={{ fontWeight: 600, fontSize: 14 }}>Recent Invoices</Text>}
            style={{ height: "100%" }}
          >
            {loadingState ? (
              <Skeleton rows={5} />
            ) : stats.recentInvoices.length ? (
              <List
                size="small"
                dataSource={stats.recentInvoices}
                renderItem={(inv) => (
                  <List.Item
                    style={{ paddingInline: 0 }}
                    extra={
                      <Badge variant={invoiceVariant(inv.status)}>
                        {inv.status}
                      </Badge>
                    }
                  >
                    <List.Item.Meta
                      title={
                        <Text strong style={{ fontSize: 13 }}>
                          {inv.invoiceNumber}
                        </Text>
                      }
                      description={
                        <Space size={4} wrap>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {inv.clientName}
                          </Text>
                          <Text type="secondary" style={{ fontSize: 12 }}>·</Text>
                          <Text style={{ fontSize: 12, color: "var(--accent-teal)", fontWeight: 600 }}>
                            {Number(inv.totalAmount ?? 0).toFixed(2)} EUR
                          </Text>
                          {inv.issuedAt && (
                            <>
                              <Text type="secondary" style={{ fontSize: 12 }}>·</Text>
                              <Text type="secondary" style={{ fontSize: 12 }}>
                                {dayjs(inv.issuedAt).format("DD MMM YYYY")}
                              </Text>
                            </>
                          )}
                        </Space>
                      }
                    />
                  </List.Item>
                )}
              />
            ) : (
              <div style={{ padding: "32px 0", textAlign: "center" }}>
                <Text type="secondary">No invoices yet.</Text>
              </div>
            )}
          </Card>
        </Col>
        )}

        {/* Recent Payments */}
        {grants.payments && (
        <Col xs={24} lg={grants.invoices ? 12 : 24}>
          <Card
            padding="sm"
            title={<Text style={{ fontWeight: 600, fontSize: 14 }}>Recent Payments</Text>}
            style={{ height: "100%" }}
          >
            {loadingState ? (
              <Skeleton rows={5} />
            ) : stats.recentPayments.length ? (
              <List
                size="small"
                dataSource={stats.recentPayments}
                renderItem={(pay) => (
                  <List.Item
                    style={{ paddingInline: 0 }}
                    extra={
                      <Text strong style={{ color: "var(--accent-teal)", fontSize: 13 }}>
                        {Number(pay.amount ?? 0).toFixed(2)} EUR
                      </Text>
                    }
                  >
                    <List.Item.Meta
                      title={
                        <Text strong style={{ fontSize: 13 }}>
                          {pay.clientName}
                        </Text>
                      }
                      description={
                        <Space size={4} wrap>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {pay.packageName}
                          </Text>
                          {pay.fromDate && (
                            <>
                              <Text type="secondary" style={{ fontSize: 12 }}>·</Text>
                              <Text type="secondary" style={{ fontSize: 12 }}>
                                {dayjs(pay.fromDate).format("DD MMM YYYY")}
                              </Text>
                            </>
                          )}
                        </Space>
                      }
                    />
                  </List.Item>
                )}
              />
            ) : (
              <div style={{ padding: "32px 0", textAlign: "center" }}>
                <Text type="secondary">No payments yet.</Text>
              </div>
            )}
          </Card>
        </Col>
        )}
      </Row>
      )}

    </Space>
  );
};

export default Dashboard;
